// scroll-utils.js

/**
 * Calculates the scroll percentage.
 * @param {number} currentScroll - The current scroll position (e.g., lenis.scroll).
 * @param {number} maxScrollOffset - The maximum scrollable distance (e.g., lenis.limit).
 * @returns {number} Scroll percentage (0-100).
 */
function calculateScrollPercentage(currentScroll, maxScrollOffset) {
  if (maxScrollOffset <= 0) {
    // No scrollable area or invalid input
    return 0;
  }
  const percentage = (currentScroll / maxScrollOffset) * 100;
  return Math.max(0, Math.min(100, percentage)); // Clamp between 0 and 100
}

/**
 * Updates the width of a given DOM element.
 * @param {HTMLElement} element - The DOM element to update.
 * @param {number} percentage - The percentage value for the width.
 */
function updateElementWidth(element, percentage) {
  if (element && typeof element.style !== 'undefined') {
    element.style.width = `${percentage}%`;
  }
}

/**
 * Initializes scroll progress indicators for elements matching the selector.
 * @param {string} selector - CSS selector for the progress indicator elements.
 * @param {object} lenisInstance - The initialized Lenis instance.
 */
let currentLenis = null;
let scrollHandler = null;
let resizeHandler = null;

export function initializeScrollProgressIndicators(selector, lenisInstance) {
  if (!lenisInstance) {
    console.error('Lenis instance not provided for scroll progress indicators.');
    return;
  }

  destroyScrollProgressIndicators(); // Ensure clean state

  const indicators = document.querySelectorAll(selector);
  if (!indicators.length) {
    // console.warn(`No scroll progress indicators found for selector: ${selector}`);
    return;
  }

  const updateIndicators = () => {
    const percentage = calculateScrollPercentage(lenisInstance.scroll, lenisInstance.limit);
    indicators.forEach(indicator => updateElementWidth(indicator, percentage));
  };

  scrollHandler = updateIndicators;
  resizeHandler = updateIndicators;
  currentLenis = lenisInstance;

  lenisInstance.on('scroll', scrollHandler);
  window.addEventListener('resize', resizeHandler); // Update on resize as limit might change
  updateIndicators(); // Initial update
}

export function destroyScrollProgressIndicators() {
  if (currentLenis && scrollHandler) {
    currentLenis.off('scroll', scrollHandler);
  }
  if (resizeHandler) {
    window.removeEventListener('resize', resizeHandler);
  }
  currentLenis = null;
  scrollHandler = null;
  resizeHandler = null;
}